import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Search, X, ChevronDown } from 'lucide-react';
import { ClientOption } from '../../services/agentBalancesService';

interface Props {
  partyType: number | null;
  onPartyTypeChange: (value: number | null) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  clientId: number | null;
  onClientIdChange: (value: number | null) => void;
  clients: ClientOption[];
  openOnly: boolean;
  onOpenOnlyChange: (value: boolean) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px 20px;
  margin-bottom: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
`;

const SegButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${p => (p.$active ? 'var(--primary-color)' : 'var(--surface-color)')};
  color: ${p => (p.$active ? '#fff' : 'var(--text-primary)')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  & + & {
    border-right: 1px solid var(--border-color);
  }

  &:hover {
    background: ${p => (p.$active ? 'var(--primary-hover)' : 'var(--hover-background)')};
  }
`;

const SearchWrap = styled.div`
  position: relative;
  width: 240px;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  display: flex;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 34px 9px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: var(--surface-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

// ----- client typeahead -----
const TypeaheadWrap = styled.div`
  position: relative;
  width: 240px;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const SelectedPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  cursor: pointer;
  font-size: 14px;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  box-shadow: var(--shadow-medium);
`;

const Option = styled.button`
  display: block;
  width: 100%;
  text-align: right;
  padding: 9px 12px;
  border: none;
  background: none;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
  }
`;

const EmptyOption = styled.div`
  padding: 9px 12px;
  font-size: 13px;
  color: var(--text-tertiary);
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary);
  display: flex;
  padding: 0;

  &:hover {
    color: var(--text-primary);
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  color: var(--text-primary);
  padding-bottom: 8px;

  input {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
    cursor: pointer;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 1px;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const ClientFilter: React.FC<{
  clients: ClientOption[];
  selectedId: number | null;
  onChange: (id: number | null) => void;
}> = ({ clients, selectedId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selected = useMemo(
    () => clients.find(c => c.id === selectedId) || null,
    [clients, selectedId]
  );

  const filtered = useMemo(() => {
    const q = query.trim();
    const list = q ? clients.filter(c => c.name.includes(q)) : clients;
    return list.slice(0, 50);
  }, [clients, query]);

  if (selected) {
    return (
      <TypeaheadWrap ref={wrapRef}>
        <SelectedPill onClick={() => onChange(null)}>
          <span>{selected.name}</span>
          <IconBtn type="button" aria-label="נקה לקוח"><X size={16} /></IconBtn>
        </SelectedPill>
      </TypeaheadWrap>
    );
  }

  return (
    <TypeaheadWrap ref={wrapRef}>
      <SearchWrap>
        <SearchIcon><ChevronDown size={16} /></SearchIcon>
        <Input
          type="text"
          placeholder="כל הלקוחות..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </SearchWrap>
      {open && (
        <Dropdown>
          {filtered.length === 0 ? (
            <EmptyOption>לא נמצאו לקוחות</EmptyOption>
          ) : (
            filtered.map(c => (
              <Option
                key={c.id}
                type="button"
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {c.name}
              </Option>
            ))
          )}
        </Dropdown>
      )}
    </TypeaheadWrap>
  );
};

const BalancesFilterBar: React.FC<Props> = ({
  partyType,
  onPartyTypeChange,
  searchInput,
  onSearchInputChange,
  clientId,
  onClientIdChange,
  clients,
  openOnly,
  onOpenOnlyChange,
  onClear,
  hasActiveFilters,
}) => {
  return (
    <Bar>
      <Field>
        <Label>סוג</Label>
        <Segmented>
          <SegButton $active={partyType === null} onClick={() => onPartyTypeChange(null)}>הכל</SegButton>
          <SegButton $active={partyType === 1} onClick={() => onPartyTypeChange(1)}>סוכנים</SegButton>
          <SegButton $active={partyType === 2} onClick={() => onPartyTypeChange(2)}>ארגונים</SegButton>
        </Segmented>
      </Field>

      <Field>
        <Label>שם סוכן / ארגון</Label>
        <SearchWrap>
          <SearchIcon><Search size={16} /></SearchIcon>
          <Input
            type="text"
            placeholder="חיפוש לפי שם..."
            value={searchInput}
            onChange={e => onSearchInputChange(e.target.value)}
          />
        </SearchWrap>
      </Field>

      <Field>
        <Label>לקוח</Label>
        <ClientFilter clients={clients} selectedId={clientId} onChange={onClientIdChange} />
      </Field>

      <Toggle>
        <input
          type="checkbox"
          checked={openOnly}
          onChange={e => onOpenOnlyChange(e.target.checked)}
        />
        הצג רק יתרה פתוחה
      </Toggle>

      <Spacer />

      {hasActiveFilters && (
        <ClearButton type="button" onClick={onClear}>
          <X size={14} /> נקה סינון
        </ClearButton>
      )}
    </Bar>
  );
};

export default BalancesFilterBar;
